import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { CARD_SX, PRIMARY_BTN_SX } from '../styles/designSystem';
import {
  invalidateAppSettingsCache,
  useAppSettings,
} from '../hooks/useAppSettings';
import { updateAppSettings } from '../services/appSettingsApi';
import type { UpdateAppSettingsRequest } from '@techgitter/contracts';
import { APP_SETTINGS_SCHEMA } from './appSettings/settingsSchema';
import type { SettingField, SettingsGroup } from './appSettings/settingsSchema';
import { SettingsFieldRenderer } from './appSettings/SettingsFieldRenderer';
import {
  buildSettingsPatch,
  getValue,
  setValue,
} from './appSettings/buildSettingsPatch';

/**
 * App Settings admin page — tabbed, schema-driven layout.
 *
 * The page reads + writes a single AppSettings JSON blob. The schema
 * (``./appSettings/settingsSchema.ts``) declares which fields belong
 * to which group; the renderer takes care of input rendering by type.
 *
 * Dirty-tracking + save are page-level (not per-tab) so jumping
 * between tabs to edit multiple groups commits in one PATCH.
 */
export const AppSettingsAdminPage: React.FC = () => {
  const { settings, isLoading, error } = useAppSettings();

  // Local draft. Initialised from `settings` once the fetch lands and
  // re-seeded whenever settings change underneath us (e.g. invalidate
  // + refetch after save).
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  const [activeTab, setActiveTab] = useState<string>(
    APP_SETTINGS_SCHEMA[0]?.id ?? '',
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!settings) return;
    setDraft({ ...settings });
  }, [settings]);

  const dirty = useMemo(() => {
    if (!draft || !settings) return false;
    const savedAsRecord = settings as unknown as Record<string, unknown>;
    return APP_SETTINGS_SCHEMA.some((group) =>
      group.fields.some(
        (f) =>
          JSON.stringify(getValue(draft, f.key)) !==
          JSON.stringify(getValue(savedAsRecord, f.key)),
      ),
    );
  }, [draft, settings]);

  const handleSave = async () => {
    if (!draft || !dirty || saving) return;
    setSaving(true);
    setSaveError(null);

    const patch = buildSettingsPatch(
      draft,
      settings ? (settings as unknown as Record<string, unknown>) : null,
      APP_SETTINGS_SCHEMA,
    );

    try {
      // Cast — `patch` is built from schema keys that map 1:1 to the
      // contract by construction; the loose Record type is for the
      // dot-path helpers, not a contract gap.
      await updateAppSettings(patch as UpdateAppSettingsRequest);
      invalidateAppSettingsCache();
      setSnackbar({
        open: true,
        message: 'Settings saved',
        severity: 'success',
      });
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to save settings',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: '12px 16px 24px' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      {isLoading || !draft ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={[CARD_SX, { p: 0, maxWidth: 880, overflow: 'hidden' }]}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            TabIndicatorProps={{ sx: { backgroundColor: '#4A90D9', height: 2.5 } }}
            sx={{
              borderBottom: '1px solid #e2eaf3',
              minHeight: 0,
              '& .MuiTab-root': {
                textTransform: 'none',
                letterSpacing: 'normal',
                fontWeight: 400,
                fontSize: 15,
                minHeight: 0,
                py: '11px',
                color: '#666666',
              },
              '& .MuiTab-root.Mui-selected': { color: '#4A90D9', fontWeight: 500 },
            }}
          >
            {APP_SETTINGS_SCHEMA.map((g) => (
              <Tab key={g.id} value={g.id} label={g.label} />
            ))}
          </Tabs>

          <Box sx={{ p: 2, pt: 1.5 }}>
            {APP_SETTINGS_SCHEMA.map((group) =>
              group.id === activeTab ? (
                <GroupContent
                  key={group.id}
                  group={group}
                  draft={draft}
                  settings={settings as unknown as Record<string, unknown>}
                  onPatch={(key, next) =>
                    setDraft((d) => (d ? setValue(d, key, next) : d))
                  }
                />
              ) : null,
            )}

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!dirty || saving}
                sx={[
                  PRIMARY_BTN_SX,
                  { '&.Mui-disabled': { bgcolor: '#4A90D9', color: '#fff', opacity: 0.4 } },
                ]}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Stack>
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Subcomponents + helpers
// ---------------------------------------------------------------------------

interface GroupContentProps {
  group: SettingsGroup;
  draft: Record<string, unknown>;
  settings: Record<string, unknown>;
  onPatch: (key: string, next: unknown) => void;
}

const GroupContent: React.FC<GroupContentProps> = ({
  group,
  draft,
  settings,
  onPatch,
}) => (
  <Stack spacing={4}>
    {group.fields.map((field) => (
      <FieldRow
        key={field.key}
        field={field}
        draft={draft}
        settings={settings}
        onPatch={onPatch}
      />
    ))}
  </Stack>
);

interface FieldRowProps {
  field: SettingField;
  draft: Record<string, unknown>;
  settings: Record<string, unknown>;
  onPatch: (key: string, next: unknown) => void;
}

const FieldRow: React.FC<FieldRowProps> = ({
  field,
  draft,
  settings,
  onPatch,
}) => {
  const draftValue = getValue(draft, field.key);
  const savedValue = getValue(settings, field.key);
  const canReset =
    JSON.stringify(draftValue) !== JSON.stringify(field.resetValue);
  const isDirty = JSON.stringify(draftValue) !== JSON.stringify(savedValue);

  // productCodes has no reset affordance (toggling the override off already
  // falls back to the built-in defaults).
  const hasReset =
    field.resetValue !== undefined && field.type !== 'productCodes';

  // Text-button reset (+ unsaved marker). Used by businessHours inline beside
  // its toggle, and as the default top-right placement for other fields.
  const resetButton =
    isDirty || hasReset ? (
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {isDirty && (
          <Box
            component="span"
            sx={{ fontSize: 11, fontWeight: 400, color: 'warning.main' }}
          >
            (unsaved)
          </Box>
        )}
        {hasReset && (
          <Button
            size="small"
            variant="text"
            startIcon={<RestartAltIcon sx={{ fontSize: 15 }} />}
            disabled={!canReset}
            onClick={() => onPatch(field.key, field.resetValue)}
            sx={{
              textTransform: 'none',
              fontWeight: 400,
              fontSize: 12,
              color: '#7a8699',
              minWidth: 0,
              px: 0.75,
              py: 0.25,
              '&:hover': { color: '#4A90D9', backgroundColor: 'transparent' },
            }}
          >
            Reset to default
          </Button>
        )}
      </Stack>
    ) : null;

  // Icon-only reset rendered inside the input control (integer fields). Only
  // shown when the value actually differs from the default.
  const resetIcon =
    hasReset && canReset ? (
      <Tooltip title="Reset to default">
        <IconButton
          size="small"
          edge="end"
          onClick={() => onPatch(field.key, field.resetValue)}
          sx={{ color: '#9aa6b6', '&:hover': { color: '#4A90D9' } }}
        >
          <RestartAltIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    ) : null;

  // businessHours: text button beside the toggle.
  // integer: icon inside the field. Everything else: top-right text button.
  const inlineReset = field.type === 'businessHours';
  const inControlReset = field.type === 'integer';

  return (
    <Box>
      {/* No section-heading label — the tab name already names the section. */}
      {!inlineReset && !inControlReset && resetButton && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          {resetButton}
        </Stack>
      )}
      {field.helperText && (
        <Typography
          sx={{
            display: 'block',
            mt: 0,
            mb: '20px',
            fontSize: 14,
            color: '#666666',
            lineHeight: 1.65,
            maxWidth: 720,
          }}
        >
          {field.helperText}
        </Typography>
      )}
      <SettingsFieldRenderer
        field={field}
        value={draftValue}
        onChange={(next) => onPatch(field.key, next)}
        resetControl={
          inlineReset ? resetButton : inControlReset ? resetIcon : undefined
        }
      />
    </Box>
  );
};
